// Industry pages: top tools as cards + expandable full collection (spec §4, Phase G).
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { featuredToolFor, toolsOwnedBy, toolsSecondaryTo } from "@/data/tools/registry";
import { track } from "@/lib/analytics";

export default function IndustryTools({ pageUrl, testid = "industry-tools" }) {
  const [open, setOpen] = useState(false);
  const featured = featuredToolFor(pageUrl);
  const owned = toolsOwnedBy(pageUrl);
  const secondary = toolsSecondaryTo(pageUrl);
  // Top set: featured first, then owned, then secondary — deduped.
  const seen = new Set();
  const top = [featured, ...owned, ...secondary].filter(Boolean).filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
  if (top.length === 0) return null;
  const topThree = top.slice(0, 3);
  const rest = top.slice(3);
  return (
    <section data-testid={testid} className="py-12 md:py-14 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex items-end justify-between gap-6 border-t-2 border-black pt-8">
          <div>
            <div className="overline">Free tools for this industry</div>
            <h2 className="mt-3 font-serif-display text-2xl md:text-3xl text-black">Try the tools before you call us</h2>
          </div>
          <Link to="/tools" data-testid={`${testid}-hub`} className="hidden sm:inline-flex text-sm font-medium underline underline-offset-4 hover:text-zinc-600">All 104 tools</Link>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {topThree.map((t) => (
            <Link
              key={t.id}
              to={t.url}
              data-testid={`${testid}-card-${t.id}`}
              onClick={() => track("tool_card_click", { tool: t.id, source: pageUrl })}
              className="group border border-zinc-200 bg-white p-5 hover:border-black transition-colors"
            >
              <div className="overline">{t.id} · Free tool</div>
              <div className="mt-2 font-serif-display text-lg leading-snug group-hover:text-zinc-700">{t.name}</div>
              <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{t.hook}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium">
                Use it free <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          ))}
        </div>
        {rest.length > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              data-testid={`${testid}-expand`}
              aria-expanded={open}
              className="inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4 hover:text-zinc-600"
            >
              {open ? "Show fewer tools" : `See all ${top.length} tools for this industry`}
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid={`${testid}-collection`}>
                {rest.map((t) => (
                  <Link
                    key={t.id}
                    to={t.url}
                    data-testid={`${testid}-more-${t.id}`}
                    onClick={() => track("tool_card_click", { tool: t.id, source: pageUrl, collection: true })}
                    className="group flex items-center justify-between gap-3 border border-zinc-200 px-4 py-3 text-sm hover:border-black transition-colors"
                  >
                    <span className="text-zinc-700 group-hover:text-black">{t.name}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-zinc-300 group-hover:text-black" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
